import * as DialogPrimitive from '@radix-ui/react-dialog';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';

interface RunPipelineConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
}

export const RunPipelineConfirmDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: RunPipelineConfirmDialogProps) => {
  const { t } = useTranslation('sony_admin');

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
      <DialogPrimitive.Content
        className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
        role="alertdialog"
        aria-modal="true"
        onEscapeKeyDown={(e) => e.stopPropagation()}
      >
        <div className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>
              <TriangleAlert className="size-4 inline bg-warning-muted rounded-lg text-warning p-2 box-content" />
              {t('sheet.pipeline_dialog.title')}
            </AlertDialogTitle>
          </AlertDialogHeader>

          <AlertDialogDescription>{t('sheet.pipeline_dialog.description')}</AlertDialogDescription>

          <Separator className="my-7" />

          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button size="md" variant="secondary" disabled={isLoading}>
                {t('sheet.pipeline_dialog.cancel')}
              </Button>
            </AlertDialogCancel>

            <Button
              size="md"
              onClick={onConfirm}
              loading={isLoading}
              data-testid="sony-pipeline-confirm-button"
            >
              {t('sheet.pipeline_dialog.confirm')}
            </Button>
          </AlertDialogFooter>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};
